export type LazyValue<T> = {
	clear(): void
	get(): T
}

/**
 * Creates a value that can be constructed lazily and later cleared for garbage
 * collection.
 */
export const createLazyValue = <T>(getLazyValue: () => T): LazyValue<T> => {
	let lazyValue: T | null = null

	return {
		get: () => (lazyValue ||= getLazyValue()),
		clear: () => {
			lazyValue = null
		},
	}
}

/**
 * Similar to `createLazyValue`, but just returns the getter function. I added
 * this since we don't have lifecycle management, and there are some cases
 * where we need to check strict referential equality. If a lazy value is
 * used and then cleared by some component, another may still have references
 * to the old value until it calls `get` again. In the case of components,
 * since we don't have a lifecycle management setup for sharing and via hooks,
 * other components holding this reference may never update.
 *
 * For now, I'm just creating this exception case. If we ever find an actual
 * performance need, where adding the logic of lazy value lifecycle
 * subscriptions and state updates is worth the cost, we can remove all of
 * this.
 */
export const createPersistentLazyValue = <T>(getLazyValue: () => T) => {
	let lazyValue: T | null = null

	return () => (lazyValue ||= getLazyValue())
}
